import { method } from './method'

/**
 * The live automation diagram on Home: a lead's
 * path through a GoHighLevel booking pipeline, drawn stage by stage as the page scrolls. The flow is
 * Kenneth's own booking-pipeline sketch (form -> booking -> reminders -> discovery call -> outcomes).
 * [CONFIRM] the panel title, the zone names and the inspector lines with Kenneth.
 *
 * Coordinates are in the diagram's own 1200 x 600 units: the main path is the top row, the call's
 * outcomes the bottom row. `plane` is who moves it: `lead` = the lead / you, `auto` = the system sends
 * it. `step` is the Brewed Method step (0 Catch, 1 Brew, 2 Pour). Nodes are listed in build order.
 */
export type FlowPlane = 'lead' | 'auto'
export type FlowIcon = 'form' | 'email' | 'booked' | 'reminder' | 'alarm' | 'call' | 'proposal' | 'won' | 'later' | 'nurture' | 'lost'

export type FlowNode = {
  id: string
  title: string
  tag: string
  plane: FlowPlane
  icon: FlowIcon
  x: number
  y: number
  w: number
  h: number
  step: number
  /** One line for the inspector. */
  about: string
}

/** `solid` links are the lead path (arrows a packet runs); `dashed` links reroute it: a reschedule loops back, the call branches out. */
export type FlowLink = { from: string; to: string; style: 'solid' | 'dashed'; label?: string }

const ROWS = [64, 376]
const box = (col: number, row: number) => ({ x: 24 + col * 200, y: ROWS[row]!, w: 156, h: 136 })

export const liveAutomation = {
  eyebrow: 'Live automation',
  titleThin: 'Catch. Brew.',
  titleBold: 'Pour.',
  steps: method.steps.map((step) => ({ name: step.name, body: step.body })),
  panel: { lead: 'Lead to', rest: 'Closed Deal' },
  legend: { auto: 'Automation', lead: 'Lead path' },
  zones: { booking: 'GoHighLevel', pipeline: 'Pipeline' },
  connected: 'System live',
  hint: { hover: 'Hover a stage to inspect', tap: 'Tap a stage to inspect' },
  nodes: [
    { id: 'form', title: 'Submit Form', tag: 'New lead trigger', plane: 'lead', icon: 'form', ...box(0, 0), step: 0, about: 'A lead fills in your form. That one submit starts everything.' },
    { id: 'email', title: 'Booking Email', tag: 'Send booking link', plane: 'auto', icon: 'email', ...box(1, 0), step: 1, about: 'Your booking link goes out on its own, while they are still interested.' },
    { id: 'booked', title: 'Client Booked', tag: 'Slot confirmed', plane: 'lead', icon: 'booked', ...box(2, 0), step: 1, about: 'They pick a slot. The call is confirmed and on your calendar.' },
    { id: 'day', title: '24hr Reminder', tag: 'Pre-appt email', plane: 'auto', icon: 'reminder', ...box(3, 0), step: 1, about: 'A reminder the day before keeps the call top of mind.' },
    { id: 'hour', title: '1hr Reminder', tag: 'Pre-appt email', plane: 'auto', icon: 'alarm', ...box(4, 0), step: 1, about: 'One more nudge an hour out. Need a new time? They rebook in a click.' },
    { id: 'call', title: 'Discovery Call', tag: 'Qualify the lead', plane: 'lead', icon: 'call', ...box(5, 0), step: 2, about: 'You show up and qualify the lead. The pipeline takes it from there.' },
    { id: 'proposal', title: 'Proposal Sent', tag: 'Scope + price', plane: 'lead', icon: 'proposal', ...box(0, 1), step: 2, about: 'Ready to buy: they get the scope and the price.' },
    { id: 'later', title: 'Maybe / Later', tag: 'Not ready yet', plane: 'lead', icon: 'later', ...box(3, 1), step: 2, about: 'Not ready yet. They stay in the system instead of going cold.' },
    { id: 'lost', title: 'Lost', tag: 'Closed out', plane: 'lead', icon: 'lost', ...box(5, 1), step: 2, about: 'Closed out cleanly, so the pipeline stays honest.' },
    { id: 'won', title: 'Won', tag: 'Deal closed', plane: 'lead', icon: 'won', ...box(1, 1), step: 2, about: 'Deal closed. The pipeline marks it won.' },
    { id: 'nurture', title: 'Nurture', tag: 'Long-term drip', plane: 'auto', icon: 'nurture', ...box(4, 1), step: 2, about: 'A long-term drip keeps you in touch until they are ready.' },
  ] satisfies FlowNode[],
  links: [
    { from: 'form', to: 'email', style: 'solid' },
    { from: 'email', to: 'booked', style: 'solid' },
    { from: 'booked', to: 'day', style: 'solid' },
    { from: 'day', to: 'hour', style: 'solid' },
    { from: 'hour', to: 'call', style: 'solid' },
    { from: 'hour', to: 'booked', style: 'dashed', label: 'Booking rescheduled' },
    { from: 'call', to: 'proposal', style: 'dashed' },
    { from: 'call', to: 'later', style: 'dashed' },
    { from: 'call', to: 'lost', style: 'dashed' },
    { from: 'proposal', to: 'won', style: 'solid' },
    { from: 'later', to: 'nurture', style: 'solid' },
  ] satisfies FlowLink[],
}
